import { AgGridReact } from '@mint-lib/ag-grid/react';
import React, {
  Dispatch,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type RefObject,
} from 'react';

import {
  CellStyleModule,
  ClientSideRowModelApiModule,
  ModuleRegistry,
  RenderApiModule,
  RowApiModule,
  RowSelectionModule,
  SelectionChangedEvent,
  ValidationModule,
  ValueFormatterParams,
  ValueGetterParams,
  type ColDef,
  type GetRowIdParams,
  type GridApi,
  type IRowNode,
  type RowSelectionOptions,
} from '@mint-lib/ag-grid/community';
import {
  ClientSideRowModelModule,
  LicenseManager,
  TreeDataModule,
  type AutoGroupColumnDef,
} from '@mint-lib/ag-grid/enterprise';
import { useChannels } from '@mint-lib/hooks';
import { useTranslation, type TFunction } from '@mint-lib/i18n';
import { usePlatforms } from '@mint-lib/routing-context';

import './grid.css';

import { Suggestions } from '@mint-sdk/api-recommendations';
import { SetStateAction } from 'jotai';

import type { SuggestionGridType } from '../../../types/suggestion.js';
import { prepareGridData } from '../../../utils/prepareSuggestionGrid.js';
import {
  CheckboxCell,
  ChipCell,
  CurrencyCell,
  ExpanderCell,
  LevelCell,
} from './cells/index.js';
import GridWrapper from './GridWrapper.jsx';

ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  ClientSideRowModelApiModule,
  TreeDataModule,
  RenderApiModule,
  RowApiModule,
  RowSelectionModule,
  ValidationModule,
  CellStyleModule,
]);

LicenseManager.setLicenseKey(import.meta.env.VITE_AG_GRID_LICENSE_KEY);

type SuggestionGridProps = {
  suggestion: Suggestions.SuggestionResponseType;
  isMultiCurrencyMode: boolean;
  isEditingMode: boolean;
  setMediaRows: Dispatch<
    SetStateAction<{
      selectedMediaRowIds: number[];
      mediaRowIds: number[];
    }>
  >;
  gridApiRef: RefObject<GridApi<SuggestionGridType> | null>;
};

const SuggestionGrid = ({
  suggestion,
  isMultiCurrencyMode,
  isEditingMode,
  setMediaRows,
  gridApiRef,
}: SuggestionGridProps) => {
  const { t } = useTranslation('@mint-lib/recommendations-circuit');
  const channels = useChannels();
  const { platformByLabel } = usePlatforms();

  const [rowData, setRowData] = useState<SuggestionGridType[]>(
    prepareGridData(suggestion, isMultiCurrencyMode),
  );

  useEffect(() => {
    // TODO: We should re-think this approach in future
    const timeout = setTimeout(() => {
      setRowData(prepareGridData(suggestion, isMultiCurrencyMode));
    }, 0);
    return () => clearTimeout(timeout);
  }, [suggestion, isMultiCurrencyMode]);

  const getBudgetSpanTrans = (t: TFunction) => ({
    lifetime: t('Lifetime'),
    daily: t('Daily'),
  });

  const getMediaRowsIds = (rowData: SuggestionGridType[]) => {
    return rowData.reduce((acc: number[], row: SuggestionGridType) => {
      if (row?.type === 'media_row' || row?.type === 'mediarow') {
        acc.push(+row.id);
      }
      return acc;
    }, []);
  };
  useEffect(() => {
    const mediaRowsIds = getMediaRowsIds(rowData);
    setMediaRows({
      selectedMediaRowIds:
        gridApiRef.current
          ?.getSelectedNodes()
          .map((node) => Number(node.data?.id)) ?? [],
      mediaRowIds: mediaRowsIds,
    });
  }, [rowData, setMediaRows]);

  const onSelectionChanged = useCallback(
    (event: SelectionChangedEvent) => {
      const selectedLeafNodes: IRowNode<SuggestionGridType>[] =
        event.api.getSelectedNodes();

      const selectedParentNodes: IRowNode<SuggestionGridType>[] = [];
      event.api.forEachNode((node) => {
        if (node.childrenAfterGroup?.length) {
          const allChildrenSelected = node.childrenAfterGroup.every((child) =>
            child.isSelected(),
          );
          if (allChildrenSelected) {
            selectedParentNodes.push(node);
          }
        }
      });

      const allSelectedNodes = [...selectedLeafNodes, ...selectedParentNodes];
      const selectedNodesRowData = allSelectedNodes.reduce(
        (acc: SuggestionGridType[], node) => {
          if (node.data) {
            acc.push(node.data);
          }
          return acc;
        },
        [],
      );
      const selectedIds = getMediaRowsIds(selectedNodesRowData);
      setMediaRows((prev) => {
        return {
          ...prev,
          selectedMediaRowIds: selectedIds,
        };
      });
    },
    [setMediaRows],
  );

  const isRowSelectable = useCallback((node: IRowNode<SuggestionGridType>) => {
    return node.data?.type !== 'channel';
  }, []);

  const rowSelection = useMemo<RowSelectionOptions>(() => {
    return {
      mode: 'multiRow',
      enableClickSelection: false,
      headerCheckbox: false,
      checkboxes: false,
      isRowSelectable,
      groupSelects: 'descendants',
    };
  }, [isRowSelectable]);

  const columnDefs = useMemo(
    () => [
      {
        cellRenderer: CheckboxCell,
        width: 48,
        pinned: true,
        hide: !isEditingMode,
      },
      {
        headerName: t('Budget span'),
        valueFormatter: (params: ValueFormatterParams) => {
          const budgetSpanText = getBudgetSpanTrans(t);
          const key =
            (params.data?.budgetType as keyof typeof budgetSpanText) ??
            'lifetime';
          return budgetSpanText[key];
        },
        cellRenderer: ChipCell,
        cellStyle: {
          display: 'flex',
          alignItems: 'center',
        },
        minWidth: 120,
        maxWidth: 200,
      },
      {
        headerName: t('Planned budget'),
        valueGetter: (params: ValueGetterParams<SuggestionGridType>) => {
          return params.data?.oldBudget;
        },
        cellRenderer: CurrencyCell,
        minWidth: 150,
        maxWidth: 200,
      },
      {
        headerName: t('Suggested budget'),
        valueGetter: (params: ValueGetterParams<SuggestionGridType>) => {
          return params.data?.newBudget;
        },
        cellRenderer: CurrencyCell,
        minWidth: 180,
        maxWidth: 200,
      },
    ],
    [isEditingMode, t],
  );
  const defaultColDef = useMemo<ColDef>(() => {
    return {
      flex: 1,
      sortable: false,
      filter: false,
      resizable: false,
      suppressHeaderMenuButton: true,
    };
  }, []);
  const autoGroupColumnDef = useMemo<
    AutoGroupColumnDef<SuggestionGridType>
  >(() => {
    return {
      headerName: t('Hierarchy'),
      headerComponent: 'expanderHeader',
      field: 'name',
      showRowGroup: true,
      cellRendererParams: {
        suppressCount: true,
        innerRenderer: LevelCell,
        channels,
        platformByLabel,
      },
      cellRenderer: 'agGroupCellRenderer',
      minWidth: 250,
    };
  }, [t, platformByLabel, channels]);

  const getRowId = useCallback(
    (params: GetRowIdParams<SuggestionGridType>) => params.data?.id.toString(),
    [],
  );

  return (
    <GridWrapper>
      <AgGridReact
        onGridReady={(p) => {
          gridApiRef.current = p.api;
        }}
        domLayout="autoHeight"
        rowData={rowData}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        autoGroupColumnDef={autoGroupColumnDef}
        getRowId={getRowId}
        treeData
        suppressMovableColumns
        treeDataParentIdField={'parentId'}
        groupDefaultExpanded={-1} // Expand all by default
        rowSelection={rowSelection}
        onSelectionChanged={onSelectionChanged}
        components={{
          expanderHeader: ExpanderCell,
        }}
        suppressContextMenu
        suppressGroupRowsSticky
        suppressColumnMoveAnimation
      />
    </GridWrapper>
  );
};
export default SuggestionGrid;
